import { authenticatedHttpClient } from './httpClient';
import type { LegalLocale } from '../models/legal';
import {
  User,
  UserCreate,
  UserUpdate,
  UserQueryResult,
  USER_DEFAULTS,
} from '../models';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  addDoc,
  setDoc,
  updateDoc,
  DocumentSnapshot,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { firestore } from '../utils/firebaseSetUp';
import { COLLECTIONS } from '../utils/collections';

// Re-export for backward compatibility
export type { User };

function isNotFoundError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  );
}

function mapUserResponse(data: User): User {
  return {
    ...data,
    created: data.created ? new Date(data.created) : undefined,
    updated: data.updated ? new Date(data.updated) : undefined,
    lastLogin: data.lastLogin ? new Date(data.lastLogin) : undefined,
    legal: data.legal
      ? {
          ...data.legal,
          acceptedAt: data.legal.acceptedAt
            ? new Date(data.legal.acceptedAt)
            : undefined,
        }
      : undefined,
  };
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Load users with pagination
 */
export const loadUsers = async (
  lastUser?: DocumentSnapshot,
  limitCount: number = 10
): Promise<UserQueryResult> => {
  let baseQuery = collection(firestore, COLLECTIONS.USERS);

  let q = query(baseQuery, orderBy('created', 'desc'), limit(limitCount));

  if (lastUser) {
    q = query(
      q,
      orderBy('created', 'desc'),
      startAfter(lastUser),
      limit(limitCount)
    );
  }

  const snapshot = await getDocs(q);

  const users: User[] = [];
  snapshot.forEach(doc => {
    const data = doc.data();
    users.push({
      ...(data as Omit<User, 'id'>),
      id: doc.id,
    });
  });

  return {
    users,
    lastDoc:
      snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length >= limitCount,
    totalCount: users.length,
  };
};

/**
 * Load admin users with pagination and filtering
 */
export const loadAdminUsers = async (
  organizationId: string,
  lastUser?: DocumentSnapshot,
  limitCount: number = 100,
  excludeUserId?: string
): Promise<UserQueryResult> => {
  try {
    const params = new URLSearchParams({
      skip: lastUser ? '1' : '0', // Simple pagination for now
      limit: limitCount.toString(),
    });

    if (excludeUserId) {
      params.append('exclude_user_id', excludeUserId);
    }

    const response = await authenticatedHttpClient.get(
      `/users/organizations/${organizationId}/users/admins?${params}`
    );

    const users = response;

    return {
      users: users.map((user: any) => ({
        ...user,
        id: user.id,
        role: user.role,
        organizationIds: user.organizationIds || [],
        active: user.active,
        panelRole: user.panelRole,
        permissions: user.permissions || [],
        effectivePermissions: user.effectivePermissions || [],
        created: new Date(user.created),
        updated: new Date(user.updated),
        lastLogin: user.lastLogin ? new Date(user.lastLogin) : undefined,
      })),
      lastDoc: null, // Server-side pagination doesn't use Firestore pagination
      hasMore: users.length >= limitCount,
      totalCount: users.length,
    };
  } catch (error: any) {
    console.error('Error loading admin users:', error);
    throw new Error(
      error.response?.data?.detail ||
        'Error al cargar los usuarios administradores'
    );
  }
};

/**
 * Get the authenticated user's profile from the server.
 */
export const getCurrentUser = async (
  organizationId?: string | null
): Promise<User | null> => {
  try {
    const path = organizationId
      ? `/users/me?organizationId=${encodeURIComponent(organizationId)}`
      : '/users/me';
    const data = await authenticatedHttpClient.get<User>(path);
    return mapUserResponse(data);
  } catch (error) {
    if (isNotFoundError(error)) {
      return null;
    }
    console.error('Error getting current user:', error);
    throw error;
  }
};

/**
 * Retry profile fetch — Google sign-up may create the user doc shortly after auth.
 */
export const getCurrentUserWithRetry = async (
  options: {
    attempts?: number;
    delayMs?: number;
    organizationId?: string | null;
  } = {}
): Promise<User | null> => {
  const { attempts = 9, delayMs = 350, organizationId } = options;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const user = await getCurrentUser(organizationId);
    if (user) {
      return user;
    }
    if (attempt < attempts - 1) {
      await sleep(delayMs);
    }
  }

  return null;
};

/**
 * Get user by ID
 */
export const getUser = async (userId: string): Promise<User | null> => {
  try {
    const data = await authenticatedHttpClient.get<User>(`/users/users/${userId}`);
    return mapUserResponse(data);
  } catch (error) {
    if (isNotFoundError(error)) {
      return null;
    }
    console.error('Error getting user:', error);
    throw error;
  }
};

export const acceptUserLegal = async (payload: {
  legalLocale: LegalLocale;
  termsVersion: string;
  privacyPolicyVersion: string;
}): Promise<User> => {
  const data = await authenticatedHttpClient.post<User>(
    '/users/me/legal-acceptance',
    {
      acceptTerms: true,
      acceptPrivacyPolicy: true,
      legalLocale: payload.legalLocale,
      termsVersion: payload.termsVersion,
      privacyPolicyVersion: payload.privacyPolicyVersion,
    }
  );
  return mapUserResponse(data);
};

function userIsOrgAdmin(user: User, organizationId: string): boolean {
  return (
    user.role === 'admin' &&
    Array.isArray(user.organizationIds) &&
    user.organizationIds.includes(organizationId)
  );
}

/**
 * Resolve a stored creator reference to an **organization admin** user (Firestore `users` collection).
 * Tries document id, email, then exact displayName — same shape of `stored` as sales vendedores.
 */
export async function fetchOrganizationAdminUserByCreatorReference(
  organizationId: string,
  stored: string
): Promise<User | null> {
  const s = (stored ?? '').trim();
  if (!s) return null;

  const usersCol = collection(firestore, COLLECTIONS.USERS);

  const byId = await getDoc(doc(usersCol, s));
  if (byId.exists()) {
    const u = { id: byId.id, ...byId.data() } as User;
    if (userIsOrgAdmin(u, organizationId)) return u;
  }

  const firstEmailMatch = async (email: string): Promise<User | null> => {
    const q = query(usersCol, where('email', '==', email), limit(10));
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      const u = { id: d.id, ...d.data() } as User;
      if (userIsOrgAdmin(u, organizationId)) return u;
    }
    return null;
  };

  if (s.includes('@')) {
    let u = await firstEmailMatch(s);
    if (!u) u = await firstEmailMatch(s.toLowerCase());
    if (u) return u;
  }

  const qName = query(usersCol, where('displayName', '==', s), limit(10));
  const nameSnap = await getDocs(qName);
  for (const d of nameSnap.docs) {
    const u = { id: d.id, ...d.data() } as User;
    if (userIsOrgAdmin(u, organizationId)) return u;
  }

  return null;
}

/**
 * Get user by Firebase Auth UID
 */
export const getUserByUid = async (uid: string): Promise<User | null> => {
  try {
    const baseQuery = collection(firestore, COLLECTIONS.USERS);
    const q = query(baseQuery, orderBy('uid'));

    const snapshot = await getDocs(q);

    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.uid === uid) {
        return {
          ...(data as Omit<User, 'id'>),
          id: doc.id,
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting user by UID:', error);
    throw error;
  }
};

/**
 * Create a new user
 */
export const createUser = async (userData: UserCreate): Promise<string> => {
  try {
    const now = new Date();
    const userWithDefaults = {
      ...userData,
      active: USER_DEFAULTS.active,
      role: userData.role || USER_DEFAULTS.role,
      emailVerified: USER_DEFAULTS.emailVerified,
      organizationIds:
        userData.organizationIds || USER_DEFAULTS.organizationIds,
      created: now,
      updated: now,
    };

    let docRef;

    // If UID is provided, use it as the document ID
    if (userData.uid) {
      const docId = userData.uid;
      docRef = doc(firestore, COLLECTIONS.USERS, docId);
      await setDoc(docRef, userWithDefaults);
    } else {
      // Fallback to auto-generated ID if no UID provided
      docRef = await addDoc(
        collection(firestore, COLLECTIONS.USERS),
        userWithDefaults
      );
    }

    return docRef.id;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update a user via API (syncs panelRole/permissions claims).
 * Pass organizationId so permission checks and panel access apply to that org.
 */
export const updateUser = async (
  userId: string,
  updates: UserUpdate,
  organizationId?: string
): Promise<User> => {
  try {
    const params = organizationId
      ? `?organizationId=${encodeURIComponent(organizationId)}`
      : '';
    const response = await authenticatedHttpClient.put<User>(
      `/users/users/${userId}${params}`,
      updates
    );
    return mapUserResponse(response);
  } catch (error: any) {
    console.error('Error updating user:', error);
    throw new Error(
      error.response?.data?.detail ||
        (error instanceof Error ? error.message : 'Error al actualizar el usuario')
    );
  }
};

/**
 * Delete a user (soft delete by setting active to false)
 */
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    const docRef = doc(firestore, COLLECTIONS.USERS, userId);
    await updateDoc(docRef, {
      active: false,
      updated: new Date(),
    });
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Get organizations for a user
 */
export const getUserOrganizations = async (
  userId: string
): Promise<string[]> => {
  try {
    const user = await getUser(userId);
    return user?.organizationIds || [];
  } catch (error) {
    console.error('Error getting user organizations:', error);
    return [];
  }
};

/**
 * Add organization to user
 */
export const addOrganizationToUser = async (
  userId: string,
  organizationId: string
): Promise<void> => {
  try {
    const user = await getUser(userId);
    if (!user) {
      throw new Error('User not found');
    }

    const organizationIds = user.organizationIds || [];
    if (!organizationIds.includes(organizationId)) {
      await updateUser(userId, {
        organizationIds: [...organizationIds, organizationId],
      });
    }
  } catch (error) {
    console.error('Error adding organization to user:', error);
    throw error;
  }
};

/**
 * Remove a user from an organization.
 * If the user only belonged to that organization, they are deleted completely.
 */
export const removeUserFromOrganization = async (
  organizationId: string,
  userId: string
): Promise<{ message: string; deleted: boolean }> => {
  try {
    const response = await authenticatedHttpClient.delete<{
      message: string;
      user?: User;
    }>(`/users/organizations/${organizationId}/users/${userId}`);

    const message =
      (response && typeof response === 'object' && 'message' in response
        ? response.message
        : undefined) || 'Usuario eliminado de la organización';

    // API omits `user` when the account was hard-deleted (last organization).
    const deleted =
      !(response && typeof response === 'object' && 'user' in response) ||
      /completely deleted/i.test(message);

    return { message, deleted };
  } catch (error: any) {
    console.error('Error removing user from organization:', error);
    throw new Error(
      error.response?.data?.detail ||
        'Error al eliminar el usuario de la organización'
    );
  }
};

/**
 * @deprecated Prefer {@link removeUserFromOrganization} — soft-updates org ids
 * without cleaning panelAccessByOrg or hard-deleting last-org users.
 */
export const removeOrganizationFromUser = async (
  userId: string,
  organizationId: string
): Promise<void> => {
  await removeUserFromOrganization(organizationId, userId);
};

/**
 * Create a new user via server API
 */
export const createUserViaAPI = async (userData: {
  email: string;
  displayName: string;
  password: string;
  organizationId: string;
  role?: string;
  panelRole?: string;
  permissions?: string[];
}): Promise<User> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/users/organizations/${userData.organizationId}/users`,
      {
        email: userData.email,
        displayName: userData.displayName,
        password: userData.password,
        role: userData.role || 'admin',
        panelRole: userData.panelRole || 'admin',
        permissions: userData.permissions,
      }
    );

    return response;
  } catch (error: any) {
    console.error('Error creating user:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al crear el usuario'
    );
  }
};

/**
 * Add existing user to organization via server API
 */
export const addExistingUserToOrganization = async (userData: {
  email: string;
  organizationId: string;
  role?: string;
  panelRole?: string;
  permissions?: string[];
}): Promise<User> => {
  try {
    const response = await authenticatedHttpClient.post(
      `/users/organizations/${userData.organizationId}/users/add-existing`,
      {
        email: userData.email,
        role: userData.role || 'admin',
        panelRole: userData.panelRole || 'admin',
        permissions: userData.permissions,
      }
    );

    return response;
  } catch (error: any) {
    console.error('Error adding user to organization:', error);
    throw new Error(
      error.response?.data?.detail ||
        'Error al agregar usuario a la organización'
    );
  }
};

/**
 * Check if user exists by email
 */
export const checkUserExists = async (email: string): Promise<User | null> => {
  try {
    const response = await authenticatedHttpClient.get(
      `/users/check-exists?email=${encodeURIComponent(email)}`
    );

    return response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null; // User doesn't exist
    }
    console.error('Error checking user existence:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al verificar si el usuario existe'
    );
  }
};

/**
 * Check if user exists and their organization membership status
 */
export const checkUserOrganizationStatus = async (
  email: string,
  organizationId: string
): Promise<{
  user: User | null;
  alreadyInOrganization: boolean;
  canAdd: boolean;
}> => {
  try {
    const response = await authenticatedHttpClient.get(
      `/users/check-organization-status?email=${encodeURIComponent(
        email
      )}&organization_id=${organizationId}`
    );

    return response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        user: null,
        alreadyInOrganization: false,
        canAdd: false,
      }; // User doesn't exist
    }
    console.error('Error checking user organization status:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al verificar el estado del usuario'
    );
  }
};
